import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import { Button } from "@mui/material";
import { gridRowsLookupSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid-premium";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import type { WorkbookRun } from "../../../../api/types/workbookRuns.types";
import { isDownloadableRun, isFinishedStatus } from "./workbookRunUtils";

interface Props {
  selectedIds: string[];
  cancelingIds: string[];
  downloadingIds: string[];
  onDownloadRuns: (ids: string[]) => void;
  onDeleteRuns: (ids: string[]) => void;
  onCancelRuns: (ids: string[]) => void;
}

const deleteDisabledTooltip = "Cancel the queued and running runs before deleting";

const WorkbookRunsToolbarActions = ({
  selectedIds,
  cancelingIds,
  downloadingIds,
  onDownloadRuns,
  onDeleteRuns,
  onCancelRuns,
}: Props) => {
  const apiRef = useGridApiContext();
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const selectedRuns = selectedIds.map((id) => rowsLookup[id]).filter((row): row is WorkbookRun => row !== undefined);
  const downloadableIds = selectedRuns.filter((run) => isDownloadableRun(run)).map((run) => run.id);
  const processingIds = selectedRuns.filter((run) => !isFinishedStatus(run.status)).map((run) => run.id);
  const canDelete = selectedRuns.length > 0 && processingIds.length === 0;

  return (
    <>
      {downloadableIds.length > 0 && (
        <Button
          loading={downloadableIds.every((id) => downloadingIds.includes(id))}
          onClick={() => onDownloadRuns(downloadableIds)}
          color="secondary"
          variant="text"
          startIcon={<SaveAltRoundedIcon />}
        >
          Download
        </Button>
      )}
      <AuthorizedBox permissions={["ManageReports"]}>
        <TooltipForDisabledAction title={canDelete ? undefined : deleteDisabledTooltip} isActionDisabled={!canDelete}>
          <Button
            disabled={!canDelete}
            color="error"
            variant="text"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => onDeleteRuns(selectedRuns.map((run) => run.id))}
          >
            Delete
          </Button>
        </TooltipForDisabledAction>
      </AuthorizedBox>
      {processingIds.length > 0 && (
        <Button
          loading={processingIds.every((id) => cancelingIds.includes(id))}
          color="error"
          variant="text"
          startIcon={<CloseRoundedIcon />}
          onClick={() => onCancelRuns(processingIds)}
        >
          {`Cancel ${pluralize("Run", processingIds.length)}`}
        </Button>
      )}
    </>
  );
};

export default WorkbookRunsToolbarActions;
